import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";

export const AddPasswordCard = () => {
  const { user, setUser } = usePortalAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user || user.has_password) return null;

  const save = async () => {
    if (password.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const res = await portalApi.setPassword(password);
      toast.success(res.message);
      setUser(res.user);
      setPassword("");
      setOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-5 border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8"
      data-testid="dashboard-add-password-card"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <KeyRound className="text-[#D4AF37] shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-serif text-lg text-white mb-2">You signed in with Google</h3>
            <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
              Add a password as a backup and you can sign in either way — useful on a device where you are not
              signed into Google.
            </p>
          </div>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            data-testid="add-password-open-btn"
            className="shrink-0 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
          >
            Add a password
          </button>
        )}
      </div>

      {open && (
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="At least 8 characters"
            data-testid="add-password-input"
            className="flex-1 bg-[#0B1220] border border-slate-800 px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid="add-password-save-btn"
            className="bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="animate-spin" size={14} />}
            Save password
          </button>
        </div>
      )}
    </div>
  );
};
